import { createControlId } from '@avantia/questionset-model';
import $ from 'jquery';
import { MouseEventHandler } from 'react';
import { triggerNavigationBarEvent } from '../../../actions/eventActions';
import { HpMenuData } from '../../../tools/addTopLevelEventListener';
import { HpOnMouseEvent } from '../../../types';
import { createTabItems, getPercentCompleted, setDocumentTitle } from './navigationTabControlCommon';
import {
  HpGetPercentCompletedResult,
  HpNavigationTabMenuControlProps,
  HpNavigationTabProps
} from './navigationTabControlTypes';

export function NavigationTabMenuControl({
  id,
  activeKey,
  highestTouchedKey,
  isJourneyComplete,
  topLevelItems: topItems,
  topLevelActiveKey,
  topLevelHigestTouchedKey,
  items,
  eventDetail,
  hasError,
  onClick
}: HpNavigationTabMenuControlProps): JSX.Element | null {
  if (items.length <= 1) {
    return null;
  }

  const { menuToggle, menuClose, menuSelect } = createNavigationTabMenuEvents(onClick);

  let topLevelActiveIndex = 0;
  const topLevelItems = topItems as HpNavigationTabProps[];
  if (topLevelItems.length > 0) {
    topLevelItems[0].submenu = items;
    topLevelItems[0].onClick = menuToggle;
    topLevelItems.forEach((item, ix) => {
      if (item.key === topLevelActiveKey) {
        topLevelActiveIndex = ix;
      }
    });
  }

  const { percentCompleted } = getPercentCompleted({
    activeKey: topLevelActiveKey,
    highestTouchedKey: topLevelHigestTouchedKey,
    items: topLevelItems
  }) as HpGetPercentCompletedResult;
  const { activeIndex, highestTouchedIndex } = getPercentCompleted({ items, activeKey, highestTouchedKey });

  setDocumentTitle(items[activeIndex]);

  return (
    <div className="av-timeline av-timeline-with-menu" id={createControlId({ questionId: id })}>
      <div className="av-timeline-line">
        <div className="av-timeline-line-complete" style={{ width: `${percentCompleted}%` }}></div>
        <div className="av-timeline-line-incomplete" style={{ width: `${100 - percentCompleted}%` }}></div>
      </div>
      <ul>
        {createTabItems({
          items: topLevelItems,
          onClick: null,
          eventDetail,
          activeIndex: topLevelActiveIndex,
          highestTouchedIndex: isJourneyComplete ? 2 : 0,
          isJourneyComplete: false,
          menuType: 'menu',
          hasError
        })}
      </ul>
      <div className="av-timeline-submenu">
        <div className="hp-triangle"></div>
        <ul onMouseLeave={menuClose}>
          {createTabItems({
            items,
            eventDetail,
            activeIndex,
            highestTouchedIndex,
            isJourneyComplete,
            hasError,
            menuType: 'submenu',
            onClick: menuSelect as HpOnMouseEvent,
            menuClose
          })}
        </ul>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createNavigationTabMenuEvents(onClick?: HpOnMouseEvent | null) {
  function menuToggle(event: React.MouseEvent<HTMLElement, MouseEvent>, toggle?: 'show' | 'hide') {
    const openClass = 'hp-open';
    const iconSelector = '.av-icon';
    const timelineSelector = '.av-timeline';
    const menuSelector = '.av-timeline-submenu';
    const tgt = $(event.currentTarget);
    let timeline = tgt.closest(timelineSelector);
    let shown = false; // if we're switching between sections, sometimes the state can become inconsistent.

    let menuDiv = $(menuSelector, timeline);
    if (toggle === 'show' || (menuDiv.length > 0 && !menuDiv.is(':visible'))) {
      const li = tgt.closest('li');
      if (li.length === 1) {
        const { top } = li.position();
        const ht = li.height() as number;
        $(iconSelector, li).addClass(openClass);
        const data: HpMenuData = { openedAt: new Date().getTime() };
        menuDiv
          .css({ top: top + ht + 'px' })
          .slideDown()
          .data(data);
        shown = true;
      }
    }

    if (!shown) {
      if (menuDiv.length === 0) {
        menuDiv = $(menuSelector);
        timeline = $(timelineSelector);
      }

      menuDiv.slideUp();
      $(iconSelector, timeline).removeClass(openClass);
    }

    if (!toggle) {
      const actionType = shown ? 'show' : 'hide';
      triggerNavigationBarEvent({ getEvent: () => event, actionType, elementType: 'menu' });
    }
  }

  const menuClose: MouseEventHandler<HTMLElement> = (event) => menuToggle(event, 'hide');

  let menuSelect: HpOnMouseEvent | undefined;
  if (onClick) {
    menuSelect = (event, detail) => {
      menuClose(event);
      triggerNavigationBarEvent({ getEvent: () => event, actionType: 'select', elementType: 'submenu' });
      if (jQuery(event.target).closest('.av-current').length !== 1) {
        onClick(event, detail);
      }
    };
  }

  return { menuToggle, menuClose, menuSelect };
}
